import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import {
	type EditorState,
	PluginKey,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';

import { getActiveColor } from './utils/color';
import { DEFAULT_COLOR } from './utils/constants';
import { getDisabledState } from './utils/disabled';

export interface CompanyHubTextColorPluginState {
	color: string | null;
	defaultColor: string;
	disabled?: boolean;
}

type CompanyHubTextColorDefaultColor = {
	color: string;
	label: string;
};

export interface CompanyHubTextColorPluginConfig {
	defaultColor?: CompanyHubTextColorDefaultColor;
}

type EditorStateConfig = Parameters<typeof EditorState.create>[0];

function createInitialPluginState(
	editorState: EditorState,
	pluginConfig?: CompanyHubTextColorPluginConfig,
): CompanyHubTextColorPluginState {
	const defaultColor = pluginConfig?.defaultColor || DEFAULT_COLOR;

	const state = {
		color: getActiveColor(editorState),
		disabled: getDisabledState(editorState),
		defaultColor: defaultColor.color,
	};

	return state;
}

export enum ACTIONS {
	RESET_COLOR,
	SET_COLOR,
	DISABLE,
}

export const pluginKey: PluginKey<CompanyHubTextColorPluginState> = new PluginKey<CompanyHubTextColorPluginState>('companyHubTextColorPlugin');

export function createPlugin(
	dispatch: Dispatch,
	pluginConfig?: CompanyHubTextColorPluginConfig,
): SafePlugin {
	return new SafePlugin<CompanyHubTextColorPluginState>({
		key: pluginKey,
		state: {
			init(_: EditorStateConfig, editorState: EditorState) {
				return createInitialPluginState(editorState, pluginConfig);
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: CompanyHubTextColorPluginState,
				_: EditorState,
				newState: EditorState,
			) {
				const meta = tr.getMeta(pluginKey) || {};

				let nextState;
				switch (meta.action) {
					case ACTIONS.RESET_COLOR:
						nextState = { ...pluginState, color: pluginState.defaultColor };
						break;

					case ACTIONS.SET_COLOR:
						nextState = { ...pluginState, color: meta.color, disabled: false };
						break;

					case ACTIONS.DISABLE:
						nextState = { ...pluginState, disabled: true };
						break;

					default:
						nextState = {
							...pluginState,
							color: getActiveColor(newState),
							disabled: getDisabledState(newState),
						};
				}

				if (
					(pluginState && pluginState.color !== nextState.color) ||
					(pluginState && pluginState.disabled !== nextState.disabled)
				) {
					dispatch(pluginKey, nextState);
					return nextState;
				}

				return pluginState;
			},
		},
		props: {
			attributes: () => ({
				'data-company-hub-text-color-plugin': 'true',
			}),
		},
	});
}
